import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerApi } from '../services/customerApi';
import type { CreateCustomerBoatRequest, UpdateCustomerBoatRequest } from '../services/customerApi';
import type { CustomerFormData } from '../types/crm.types';

// Query keys for customer data
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...customerKeys.lists(), filters] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  leads: (id: string) => [...customerKeys.detail(id), 'leads'] as const,
  quotes: (id: string) => [...customerKeys.detail(id), 'quotes'] as const,
  boats: (id: string) => [...customerKeys.detail(id), 'boats'] as const,
  enquiries: (id: string) => [...customerKeys.detail(id), 'enquiries'] as const,
  stats: (id: string) => [...customerKeys.detail(id), 'stats'] as const,
};

/**
 * Hook to fetch customers list with filters
 */
export function useCustomers(filters: Record<string, any> = {}) {
  return useQuery({
    queryKey: customerKeys.list(filters),
    queryFn: () => customerApi.getCustomers(filters),
    // #1058 — always refetch when this list becomes active again. The shared
    // client's 5-minute staleTime meant navigating back via the side menu (or
    // switching Customers↔Leads, which only changes the query key) served a
    // cached page — a customer minted by a sold quote stayed invisible until a
    // hard refresh, because nothing cross-domain invalidates customerKeys.
    refetchOnMount: 'always',
  });
}

/**
 * Hook to fetch single customer
 */
export function useCustomer(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => customerApi.getCustomer(id),
    enabled: !!id,
  });
}

/**
 * Hook to create customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CustomerFormData) => customerApi.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/**
 * Hook to update customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomerFormData> }) =>
      customerApi.updateCustomer(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/**
 * Hook to delete customer
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerApi.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/**
 * Hook to restore customer
 */
export function useRestoreCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerApi.restoreCustomer(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/**
 * Hook to get customer's leads
 */
export function useCustomerLeads(id: string, params?: Record<string, any>) {
  return useQuery({
    queryKey: customerKeys.leads(id),
    queryFn: () => customerApi.getCustomerLeads(id, params),
    enabled: !!id,
  });
}

/**
 * Hook to get customer's quotes
 */
export function useCustomerQuotes(id: string, params?: Record<string, any>) {
  return useQuery({
    queryKey: customerKeys.quotes(id),
    queryFn: () => customerApi.getCustomerQuotes(id, params),
    enabled: !!id,
  });
}

/**
 * Hook to get customer's inquiries — #1091, the customer page's Inquiries tab.
 */
export function useCustomerEnquiries(id: string, params?: Record<string, any>) {
  return useQuery({
    queryKey: customerKeys.enquiries(id),
    queryFn: () => customerApi.getCustomerEnquiries(id, params),
    enabled: !!id,
  });
}

/**
 * Hook to get customer's boats
 */
export function useCustomerBoats(id: string, params?: Record<string, any>) {
  return useQuery({
    queryKey: customerKeys.boats(id),
    queryFn: () => customerApi.getCustomerBoats(id, params),
    enabled: !!id,
  });
}

/**
 * Hook to get customer statistics
 */
export function useCustomerStats(id: string) {
  return useQuery({
    queryKey: customerKeys.stats(id),
    queryFn: () => customerApi.getCustomerStats(id),
    enabled: !!id,
  });
}

/**
 * D9-12 (#408) — the walk-in customer-boat create. This and
 * `useUpdateCustomerBoat` are the only customer-boat WRITE path in the app;
 * the quote-sale path (`QuoteStateTransitionService::attachInventoryBoatToCustomer`)
 * writes `customer_boats` directly on the server and is never routed through
 * here. No toast here — a 409 must land on the dialog's HIN field, not a toast.
 */
export function useCreateCustomerBoat(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerBoatRequest) => customerApi.createBoat(customerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.boats(customerId) });
    },
  });
}

/**
 * D9-11 (#408) — the add-the-HIN-later update. See `useCreateCustomerBoat`'s
 * comment above: this pair is the only customer-boat WRITE path in the app.
 */
export function useUpdateCustomerBoat(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerBoatRequest }) =>
      customerApi.updateBoat(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.boats(customerId) });
    },
  });
}
